import React from "react";
import { CreditReportProfile } from "../../API/CreditScore";

type CreditReportSectionProps = {
  report: CreditReportProfile | null;
  loading: boolean;
};

const formatCurrency = (value?: number | string | null) => {
  if (value === null || value === undefined || value === "") return "—";
  return `£${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const formatPercent = (value?: number | string | null) => {
  if (value === null || value === undefined || value === "") return "—";
  return `${Number(value)}%`;
};

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString();
};

const formatScore = (value?: number | null) => {
  if (value === null || value === undefined) return "—";
  return String(value);
};

const ReadOnlyField = ({
  label,
  value,
  className = "col-md-3",
}: {
  label: string;
  value: string | number;
  className?: string;
}) => (
  <div className={className}>
    <label className="form-label">{label}</label>
    <input type="text" className="form-control" value={value} disabled readOnly />
  </div>
);

const CreditReportSection: React.FC<CreditReportSectionProps> = ({
  report,
  loading,
}) => {
  if (loading) {
    return (
      <div className="credit-report-section mt-5">
        <h4 className="info-title-text">Credit Report</h4>
        <p className="credit-report-empty">Loading credit report...</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="credit-report-section mt-5">
        <h4 className="info-title-text">Credit Report</h4>
        <p className="credit-report-empty">
          No credit report available yet. Complete CheckMyFile credit assessment during onboarding.
        </p>
      </div>
    );
  }

  const creditLimit =
    report.utilization?.totalCreditLimit ?? report.totalCreditLimit;
  const totalBalance =
    report.utilization?.totalBalance ?? report.totalBalance;
  const utilizationPercent =
    report.utilization?.utilizationPercent ?? report.utilizationPercent;

  return (
    <div className="credit-report-section mt-5">
      <h4 className="info-title-text">Credit Report</h4>

      <div className="credit-report-card">
        <h5 className="credit-report-subtitle">Summary</h5>
        <div className="row g-3 mb-4">
          <ReadOnlyField label="Credit Score" value={formatScore(report.creditScore)} />
          <ReadOnlyField label="Grade" value={report.grade || "—"} className="col-md-2" />
          <ReadOnlyField
            label="Report Date"
            value={formatDate(report.reportDate || report.uploadedAt)}
            className="col-md-3"
          />
          <ReadOnlyField
            label="Utilization"
            value={formatPercent(utilizationPercent)}
            className="col-md-2"
          />
          <ReadOnlyField
            label="Total Credit Limit"
            value={formatCurrency(creditLimit)}
            className="col-md-3"
          />
          <ReadOnlyField
            label="Total Balance"
            value={formatCurrency(totalBalance)}
            className="col-md-3"
          />
        </div>

        <h5 className="credit-report-subtitle">Bureau Scores</h5>
        <div className="row g-3 mb-4">
          <ReadOnlyField
            label="Experian"
            value={formatScore(report.bureauScores?.experian ?? null)}
          />
          <ReadOnlyField
            label="Equifax"
            value={formatScore(report.bureauScores?.equifax ?? null)}
          />
          <ReadOnlyField
            label="TransUnion"
            value={formatScore(report.bureauScores?.transUnion ?? null)}
          />
          <ReadOnlyField
            label="Active Loans"
            value={report.summary?.totalActiveLoans ?? "—"}
          />
        </div>

        <h5 className="credit-report-subtitle">Payment History Summary</h5>
        <div className="row g-3 mb-4">
          <ReadOnlyField
            label="Total Outstanding"
            value={formatCurrency(report.summary?.totalOutstanding)}
          />
          <ReadOnlyField
            label="Missed Payments"
            value={report.summary?.missedPaymentsCount ?? "—"}
          />
          <ReadOnlyField
            label="On-time Payment Rate"
            value={formatPercent(report.summary?.onTimePaymentRate)}
          />
          <ReadOnlyField
            label="Uploaded At"
            value={formatDate(report.uploadedAt)}
          />
        </div>

        {report.loans && report.loans.length > 0 && (
          <div className="credit-report-table-block mb-4">
            <h5 className="credit-report-subtitle">Loans & Accounts</h5>
            <div className="table-responsive">
              <table className="table credit-report-table">
                <thead>
                  <tr>
                    <th>Lender</th>
                    <th>Type</th>
                    <th>Credit Limit</th>
                    <th>Outstanding</th>
                    <th>Original Amount</th>
                    <th>Interest Rate</th>
                    <th>EMI</th>
                    <th>Status</th>
                    <th>Opened</th>
                  </tr>
                </thead>
                <tbody>
                  {report.loans.map((loan, index) => (
                    <tr key={loan.uuid || index}>
                      <td>{loan.lenderName || "—"}</td>
                      <td className="text-capitalize">
                        {(loan.accountType || "—").replace(/_/g, " ")}
                      </td>
                      <td>{formatCurrency(loan.creditLimit)}</td>
                      <td>{formatCurrency(loan.outstandingBalance)}</td>
                      <td>{formatCurrency(loan.originalAmount)}</td>
                      <td>
                        {loan.interestRate !== null && loan.interestRate !== undefined
                          ? `${loan.interestRate}%`
                          : "—"}
                      </td>
                      <td>{formatCurrency(loan.emiAmount)}</td>
                      <td className="text-capitalize">{loan.status || "—"}</td>
                      <td>{formatDate(loan.openedDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {report.payments && report.payments.length > 0 && (
          <div className="credit-report-table-block">
            <h5 className="credit-report-subtitle">Payments</h5>
            <div className="table-responsive">
              <table className="table credit-report-table">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Due Date</th>
                    <th>Paid Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {report.payments.map((payment, index) => (
                    <tr key={payment.uuid || index}>
                      <td>{payment.accountRef || "—"}</td>
                      <td>{formatDate(payment.dueDate)}</td>
                      <td>{formatDate(payment.paidDate)}</td>
                      <td>{formatCurrency(payment.amount)}</td>
                      <td className="text-capitalize">{payment.status || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {report.validationWarnings && report.validationWarnings.length > 0 && (
          <div className="credit-report-warnings mt-3">
            <h5 className="credit-report-subtitle">Validation Warnings</h5>
            <ul>
              {report.validationWarnings.map((warning, index) => (
                <li key={index}>{warning.message}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditReportSection;
